/**
 * SelectDropdown.tsx
 *
 * This component provides a customizable multi-select dropdown using the `react-multi-select-component` library.
 * It supports both single-select and multi-select options, and includes optional features like labels, loaders, and custom styling.
 *
 * Key Features:
 * - Supports multi-select and single-select modes.
 * - Allows passing a custom set of options and selected data.
 * - Optional features include labels, required fields, and dynamic classNames.
 *
 * File Name: SelectDropdown.tsx
 * Date: 25-07-24
 */

import React from "react";
import { MultiSelect, Option } from "react-multi-select-component";
import { convertToKebabCase } from "src/helpers/helperFunctions";

// Define the prop types for SelectDropdown

export interface IDefaultItemRendererProps {
  checked: boolean;
  option: Option;
  disabled?: boolean;
  onClick: () => void;
  id?: string;
}

interface ISelectDropdownProps {
  loader?: boolean;
  options: Option[];
  isMulti?: boolean;
  data: Option[];
  setData: (data: Option[]) => void;
  checkboxShow?: boolean;
  isLabel?: boolean;
  labelText?: string;
  isRequired?: boolean;
  extraClass?: string; // Correct type for CSS styles
  placeholder?: { [key: string]: string };
  clearIcon?: boolean;
  isDisabled?: boolean;
  customRendrer?: (props: IDefaultItemRendererProps) => React.ReactNode;
  disableSearch?: boolean;
  error?: string;
  id?: string;
  dropdownMainClass?: string;
}



export const CustomDropdownItemRenderer = ({
  checked,
  option,
  onClick,
  disabled,
  id,
}: IDefaultItemRendererProps) => {
  return (
    <div className={`item-renderer p-[10px] ${disabled ? "disabled" : ""}`} onClick={onClick} id={id ? id : `option-${convertToKebabCase(option?.label)}`}>
      <input type="checkbox" onChange={onClick} checked={checked} tabIndex={-1} disabled={disabled} />
      <span>{option?.label}</span>
    </div>
  );
};

const SelectDropdown: React.FC<ISelectDropdownProps> = ({
  loader = false,
  options = [],
  isMulti = false,
  data = [],
  setData = () => {},
  checkboxShow = false,
  isLabel = false,
  labelText = "",
  isRequired = false,
  extraClass = "",
  placeholder = undefined,
  clearIcon = true,
  isDisabled = false,
  customRendrer = undefined,
  disableSearch = true,
  error = "",
  id = "",
  dropdownMainClass = "",
}) => {
  const handleChange = (e: Option[]) => {
    if (isMulti) {
      setData(e);
    } else {
      if (e.length > 1) {
        setData([e[e.length - 1]]);
      } else {
        setData(e);
      }
    }
  };

  return (
    <div className={`select-dropdown-container ${dropdownMainClass}`}>
      {isLabel && (
        <label className="font-size-18px label-color-select-city font-Poppins-Regular">
          {isRequired && <span className="text-[#FF0000]">* </span>}
          {labelText}
        </label>
      )}
      <MultiSelect
        {...(!id && { id: id })}
        // defaultIsOpen={true}           // Opens the dropdown by default
        // closeOnChangedValue={false}
        disabled={isDisabled}
        isLoading={loader}
        options={options}
        value={data}
        onChange={handleChange}
        labelledBy="Re Select"
        hasSelectAll={false}
        // defaultIsOpen={true}
        // closeOnChangedValue={true}
        closeOnChangedValue={!isMulti}
        // valueRenderer={customRendrer}
        valueRenderer={(selected: Option[]) => {
          // Check if all options are selected
          if (selected.length === options.length) {
            return (
              <>
                {options?.map((v) => v.label)?.join(",")}
                {/* {options.map((options, index) => (
                  <span key={index}>{options.label},</span>
                ))} */}
              </>
            );
          }
        }}
        disableSearch={disableSearch} // Optional: Disable search if not needed
        className={`${extraClass} ${checkboxShow ? "" : "checkboxHide"}`}
        overrideStrings={placeholder}
        {...(!clearIcon && { clearIcon: null, ClearSelectedIcon: null })} // Conditionally add properties if clearIcon is false
        ItemRenderer={customRendrer}
        // ItemRenderer={}
      />
      {error && (
        <p className="text-red-500 font-size-14px font-Poppins-Regular">
          {error}
        </p>
      )}
    </div>
  );
};

export default SelectDropdown;
